import React, {useEffect} from "react";
import { Form, Button, Stack, Row, Col, Spinner } from "react-bootstrap";
import {NotificationExtra} from "Stores/superStore";
import { useNotifications } from "@/pages/preferences/hooks/notifications";
import {useAppContext} from "@/contexts/app";
import {EAIClient} from "Services/EAIClient";
import { useSchoolSettings } from "@/hooks/schools";
import PageTitle from "Components/Common/PageTitle";
import { useToast } from "@/hooks/useToast";
import { Utils } from "@/common/utils";
import NewUiChoice from "@/pages/preferences/components/new-ui-choice";

export default function PreferencesPage() {
    const [isUpdatingPreferences, setIsUpdatingPreferences] = React.useState(false);
    const [hasChanges, setHasChanges] = React.useState(false);
    const [newAtRiskThreshold, setNewAtRiskThreshold] = React.useState<number>(0);
    const [sentToFaculty, setSentToFaculty] = React.useState(false);
    const [sentToMentors, setSentToMentors] = React.useState(false);
    const [includeQuizzes, setIncludeQuizzes] = React.useState(false);
    const { toast } = useToast();

    const [actionDueOneDay, setActionDueOneDay] = React.useState<NotificationExtra>({
        event: "action_due_in_1_day",
        admins: false,
        assignee: false,
        collaborator: false,
        committee_member: false,
        owner: false
    });
    const [actionOverdue, setActionOverdue] = React.useState<NotificationExtra>({
        event: "action_overdue",
        admins: false,
        assignee: false,
        collaborator: false,
        committee_member: false,
        owner: false
    });
    const [actionMidpoint, setActionMidpoint] = React.useState<NotificationExtra>({
        event: "action_midpoint",
        admins: false,
        assignee: false,
        collaborator: false,
        committee_member: false,
        owner: false
    });
    const {data: notificationSettings, mutate: mutateNotificationsSettings} = useNotifications();
    const {data: schoolSettings, mutate: mutateSchoolSettings } = useSchoolSettings();
    const appContext = useAppContext();
    const eaiClient = new EAIClient();
    const handleCancel = async () => {
        loadSettings();
        setNewAtRiskThreshold(schoolSettings?.at_risk_threshold || 0);
        setHasChanges(false);
    };

    const handleSave = async () => {
        try {
            setIsUpdatingPreferences(true);
            await Promise.all([
                eaiClient.updateNotificationSettings({
                    send_new_assessments_to_faculty: sentToFaculty,
                    send_new_assessments_to_mentors: sentToMentors,
                    include_quizzes: includeQuizzes,
                    notifications_extra: [
                        actionDueOneDay,
                        actionMidpoint,
                        actionOverdue
                    ],
                }),
                eaiClient.updatePassingGradeSettings(newAtRiskThreshold)
            ]);
            toast({
                type: "success",
                title: "Preferences Updated",
                message: "Account preferences successfully updated."
            });
            setHasChanges(false);
            await Promise.all([
                mutateNotificationsSettings(),
                mutateSchoolSettings()
            ]);
        } catch (e) {
            appContext.dispatchError(e);
        } finally {
            setIsUpdatingPreferences(false);
        }
    };

    const loadSettings = () => {
        const {
            send_new_assessments_to_faculty,
            send_new_assessments_to_mentors,
            include_quizzes,
            notifications_extra,
        } = notificationSettings!;
        const newActionDueOneDay = notifications_extra.find(notification => notification.event === "action_due_in_1_day") || actionDueOneDay;
        const newActionOverdue = notifications_extra.find(notification => notification.event === "action_overdue") || actionOverdue;
        const newActionMidpoint = notifications_extra.find(notification => notification.event === "action_midpoint") || actionMidpoint;
        setSentToFaculty(send_new_assessments_to_faculty);
        setSentToMentors(send_new_assessments_to_mentors);
        setIncludeQuizzes(include_quizzes);
        setActionDueOneDay(newActionDueOneDay);
        setActionOverdue(newActionOverdue);
        setActionMidpoint(newActionMidpoint);
    };

    useEffect(() => {
        if (!notificationSettings) {
            return;
        }
        loadSettings();
    }, [notificationSettings]);

    useEffect(() => {
        if (schoolSettings) {
            setNewAtRiskThreshold(schoolSettings.at_risk_threshold);
        }
    }, [schoolSettings]);

    return (
        <>
            <PageTitle pageTitle="Preferences" />
            <Stack gap={0}>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h1 className="h3 mb-0 fw-bold" style={{lineHeight: "32px", letterSpacing: "1%"}}>Preferences</h1>
                    <div className="d-flex gap-2">
                        <Button
                            id="notifications-cancel-preferences-button"
                            variant="outline-secondary"
                            onClick={handleCancel}
                            disabled={isUpdatingPreferences || !hasChanges}>Cancel</Button>
                        <Button
                            id="notifications-save-preferences-button"
                            style={{
                                backgroundColor: "#F4B86F",
                                borderColor: "#F4B86F",
                                color: "black",
                            }}
                            className="px-4"
                            disabled={isUpdatingPreferences || !hasChanges}
                            onClick={handleSave}
                        >
                            {isUpdatingPreferences && <Spinner size="sm" />} Save preferences
                        </Button>
                    </div>
                </div>

                <Stack gap={3} style={{maxWidth: "800px"}}>
                    {/* Passing Grade Section */}
                    <div>
                        <h2 className="h5 mb-2 fw-bold">Passing grade</h2>
                        <p className="text-secondary mb-3">
                            Set the minimum grade required to pass. This setting will affect all dashboards across the platform.
                        </p>
                        <Form.Group controlId="passingGrade">
                            <Form.Label className="fw-bold">Passing grade</Form.Label>
                            <Form.Control
                                type="number"
                                value={newAtRiskThreshold}
                                onChange={(e) => {
                                    const value = parseFloat(e.target.value);
                                    if (value >= 1 && value <= 99) {
                                        setNewAtRiskThreshold(value);
                                        setHasChanges(true);
                                    }
                                }}
                                style={{maxWidth: "400px"}}
                                min={1}
                                max={99}
                            />
                        </Form.Group>
                    </div>

                    <hr className="my-4" />

                    {/* New Assessment Emails Section */}
                    <div>
                        <h2 className="h5 mb-2 fw-bold">New Assessment Emails</h2>
                        <p className="text-secondary mb-3">
                            Select the recipient for notifications.
                        </p>
                        <Stack gap={3} className="ps-2">
                            <Form.Group>
                                <Form.Check
                                    type="checkbox"
                                    id="sendToFaculty"
                                    checked={sentToFaculty}
                                    onChange={() => {
                                        setSentToFaculty(!sentToFaculty);
                                        setHasChanges(true);
                                    }}
                                    label={
                                        <div className="ms-2">
                                            <div className="fw-bold">Send to faculty</div>
                                        </div>
                                    }
                                />
                            </Form.Group>

                            <Form.Group>
                                <Form.Check
                                    type="checkbox"
                                    id="sendToMentor"
                                    checked={sentToMentors}
                                    onChange={() => {
                                        setSentToMentors(!sentToMentors);
                                        setHasChanges(true);
                                    }}
                                    label={
                                        <div className="ms-2">
                                            <div className="fw-bold">Send to mentors</div>
                                        </div>
                                    }
                                />
                            </Form.Group>

                            <Form.Group>
                                <Form.Check
                                    type="checkbox"
                                    id="includeQuizzes"
                                    checked={includeQuizzes}
                                    onChange={() => {
                                        setIncludeQuizzes(!includeQuizzes);
                                        setHasChanges(true);
                                    }}
                                    label={
                                        <div className="ms-2">
                                            <div className="fw-bold">Include Quizzes</div>
                                        </div>
                                    }
                                />
                            </Form.Group>
                        </Stack>
                    </div>

                    <hr className="my-4" />

                    {/* ActionPlan Emails Section */}
                    <div>
                        <h2 className="h5 mb-2 fw-bold">ActionPlan Emails</h2>
                        <p className="text-secondary mb-3">
                            Select the recipient for notifications.
                        </p>
                        <Form.Group className="mb-4">
                            <Row className="align-items-start">
                                <Col xs={3}>
                                    <Form.Label className="fw-bold">Owner</Form.Label>
                                </Col>
                                <Col xs={9}>
                                    <Stack gap={2}>
                                        <Form.Check
                                            type="checkbox"
                                            name="actionDueOneDayOwner"
                                            id="actionDueOneDayOwner"
                                            checked={actionDueOneDay.owner}
                                            onChange={() => {
                                                setActionDueOneDay({
                                                    ...actionDueOneDay,
                                                    owner: !actionDueOneDay.owner,
                                                });
                                                setHasChanges(true);
                                            }}
                                            label="One day remaining" />
                                        <Form.Check
                                            type="checkbox"
                                            name="actionMidpointOwner"
                                            id="actionMidpointOwner"
                                            checked={actionMidpoint.owner}
                                            onChange={() => {
                                                setActionMidpoint({ ...actionMidpoint, owner: !actionMidpoint.owner });
                                                setHasChanges(true);
                                            }}
                                            label="Midpoint" />
                                        <Form.Check
                                            type="checkbox"
                                            name="actionOverdueOwner"
                                            id="actionOverdueOwner"
                                            checked={actionOverdue.owner}
                                            onChange={() => {
                                                setActionOverdue({ ...actionOverdue, owner: !actionOverdue.owner });
                                                setHasChanges(true);
                                            }}
                                            label="Overdue" />
                                    </Stack>
                                </Col>
                            </Row>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Row className="align-items-start">
                                <Col xs={3}>
                                    <Form.Label className="fw-bold">Assignee</Form.Label>
                                </Col>
                                <Col xs={9}>
                                    <Stack gap={2}>
                                        <Form.Check
                                            type="checkbox"
                                            name="actionDueOneDayAssignee"
                                            id="actionDueOneDayAssignee"
                                            checked={actionDueOneDay.assignee}
                                            onChange={() => {
                                                setActionDueOneDay({
                                                    ...actionDueOneDay,
                                                    assignee: !actionDueOneDay.assignee,
                                                });
                                                setHasChanges(true);
                                            }}
                                            label="One day remaining" />
                                        <Form.Check
                                            type="checkbox"
                                            name="actionMidpointAssignee"
                                            id="actionMidpointAssignee"
                                            checked={actionMidpoint.assignee}
                                            onChange={() => {
                                                setActionMidpoint({
                                                    ...actionMidpoint,
                                                    assignee: !actionMidpoint.assignee,
                                                });
                                                setHasChanges(true);
                                            }}
                                            label="Midpoint" />
                                        <Form.Check
                                            type="checkbox"
                                            name="actionOverdueAssignee"
                                            id="actionOverdueAssignee"
                                            checked={actionOverdue.assignee}
                                            onChange={() => {
                                                setActionOverdue({
                                                    ...actionOverdue,
                                                    assignee: !actionOverdue.assignee,
                                                });
                                                setHasChanges(true);
                                            }}
                                            label="Overdue" />
                                    </Stack>
                                </Col>
                            </Row>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Row className="align-items-start">
                                <Col xs={3}>
                                    <Form.Label className="fw-bold">Collaborator</Form.Label>
                                </Col>
                                <Col xs={9}>
                                    <Stack gap={2}>
                                        <Form.Check
                                            type="checkbox"
                                            name="actionDueOneDayCollaborator"
                                            id="actionDueOneDayCollaborator"
                                            checked={actionDueOneDay.collaborator}
                                            onChange={() => {
                                                setActionDueOneDay({
                                                    ...actionDueOneDay,
                                                    collaborator: !actionDueOneDay.collaborator,
                                                });
                                                setHasChanges(true);
                                            }}
                                            label="One day remaining" />
                                        <Form.Check
                                            type="checkbox"
                                            name="actionMidpointCollaborator"
                                            id="actionMidpointCollaborator"
                                            checked={actionMidpoint.collaborator}
                                            onChange={() => {
                                                setActionMidpoint({
                                                    ...actionMidpoint,
                                                    collaborator: !actionMidpoint.collaborator,
                                                });
                                                setHasChanges(true);
                                            }}
                                            label="Midpoint" />
                                        <Form.Check
                                            type="checkbox"
                                            name="actionOverdueCollaborator"
                                            id="actionOverdueCollaborator"
                                            checked={actionOverdue.collaborator}
                                            onChange={() => {
                                                setActionOverdue({
                                                    ...actionOverdue,
                                                    collaborator: !actionOverdue.collaborator,
                                                });
                                                setHasChanges(true);
                                            }}
                                            label="Overdue" />
                                    </Stack>
                                </Col>
                            </Row>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Row className="align-items-start">
                                <Col xs={3}>
                                    <Form.Label className="fw-bold">Committee</Form.Label>
                                </Col>
                                <Col xs={9}>
                                    <Stack gap={2}>
                                        <Form.Check
                                            type="checkbox"
                                            name="actionDueOneDayCommittee"
                                            id="actionDueOneDayCommittee"
                                            checked={actionDueOneDay.committee_member}
                                            onChange={() => {
                                                setActionDueOneDay({
                                                    ...actionDueOneDay,
                                                    committee_member: !actionDueOneDay.committee_member,
                                                });
                                                setHasChanges(true);
                                            }}
                                            label="One day remaining" />
                                        <Form.Check
                                            type="checkbox"
                                            name="actionMidpointCommittee"
                                            id="actionMidpointCommittee"
                                            checked={actionMidpoint.committee_member}
                                            onChange={() => {
                                                setActionMidpoint({
                                                    ...actionMidpoint,
                                                    committee_member: !actionMidpoint.committee_member,
                                                });
                                                setHasChanges(true);
                                            }}
                                            label="Midpoint" />
                                        <Form.Check
                                            type="checkbox"
                                            name="actionOverdueCommittee"
                                            id="actionOverdueCommittee"
                                            checked={actionOverdue.committee_member}
                                            onChange={() => {
                                                setActionOverdue({
                                                    ...actionOverdue,
                                                    committee_member: !actionOverdue.committee_member,
                                                });
                                                setHasChanges(true);
                                            }}
                                            label="Overdue" />
                                    </Stack>
                                </Col>
                            </Row>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Row className="align-items-start">
                                <Col xs={3}>
                                    <Form.Label className="fw-bold">Admin</Form.Label>
                                </Col>
                                <Col xs={9}>
                                    <Stack gap={2}>
                                        <Form.Check
                                            type="checkbox"
                                            name="actionDueOneDayAdmins"
                                            id="actionDueOneDayAdmins"
                                            checked={actionDueOneDay.admins}
                                            onChange={() => {
                                                setActionDueOneDay({
                                                    ...actionDueOneDay,
                                                    admins: !actionDueOneDay.admins,
                                                });
                                                setHasChanges(true);
                                            }}
                                            label="One day remaining" />
                                        <Form.Check
                                            type="checkbox"
                                            name="actionMidpointAdmins"
                                            id="actionMidpointAdmins"
                                            checked={actionMidpoint.admins}
                                            onChange={() => {
                                                setActionMidpoint({ ...actionMidpoint, admins: !actionMidpoint.admins });
                                                setHasChanges(true);
                                            }}
                                            label="Midpoint" />
                                        <Form.Check
                                            type="checkbox"
                                            name="actionOverdueAdmins"
                                            id="actionOverdueAdmins"
                                            checked={actionOverdue.admins}
                                            onChange={() => {
                                                setActionOverdue({ ...actionOverdue, admins: !actionOverdue.admins });
                                                setHasChanges(true);
                                            }}
                                            label="Overdue" />
                                    </Stack>
                                </Col>
                            </Row>
                        </Form.Group>
                    </div>

                    {/* Moving a whole subdomain to the new UI is limited to superadmins,
                        tighter than the plain-admin gate on the rest of this page
                        (and tighter than the API endpoint allows). */}
                    {Utils.isSuperAdmin() && <>
                        <hr className="my-4" />
                        <NewUiChoice />
                    </>}
                </Stack>
            </Stack>
        </>
    );
}

